import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { usePerfilProfissional } from 'hooks/usePerfilProfissional';

/**
 * O modo da conta de quem tem perfil profissional: "trabalhando" ou "contratando".
 *
 * <h2>Por que existe</h2>
 * O domínio nunca separou as duas coisas: é uma conta com um perfil profissional que se liga e
 * desliga, e o papel sai de cada agendamento (`PapelNaAgenda` no backend). A busca só esconde a
 * própria pessoa, e `/agenda/add` aceita profissional como cliente. O front é que partia da premissa
 * "o profissional não usa a busca": tirava dela a aba Buscar, o "Buscar profissional" do menu e o
 * painel do lado de quem contrata. A profissional que queria uma faxina em casa não tinha caminho.
 *
 * O modo é o padrão do Airbnb ("modo anfitrião / modo viagem"): ele troca o painel e a navegação,
 * nunca as permissões. Cliente sem perfil profissional não tem modo; ele é sempre "contratando".
 *
 * <h2>Onde mora o valor</h2>
 * Estado de módulo mais `useSyncExternalStore`, como o avatar (useAvatarUsuario): NavBar, barra de
 * abas e painel leem o mesmo valor e mudam juntos na troca, sem Provider e sem F5. Todo login começa
 * em "trabalhando" (voltarAoModoPadrao). Dentro da sessão, inclusive num F5 ou ao reabrir o app, o
 * último modo fica no localStorage, por usuário: a conta trocada no mesmo aparelho não herda o modo da anterior. É só
 * conveniência. Se o storage falhar, o modo volta ao padrão e nada quebra.
 *
 * Ver prompts/modo-trabalhando-contratando.md.
 */
export const MODO = Object.freeze({ TRABALHANDO: 'trabalhando', CONTRATANDO: 'contratando' });

const modos = new Map();
// Quantas pendências o modo que NÃO está na tela tem: pedidos de clientes esperando resposta (para
// quem está contratando) ou faxinas dela esperando avaliação (para quem está trabalhando). Quem
// busca é o painel; NavBar e barra de abas só leem, sem requisição a mais a cada navegação.
const pendenciasForaDaTela = new Map();
const ouvintes = new Set();

const chaveDoStorage = (userId) => `novafaxina:modo:${userId}`;
const ehModo = (valor) => valor === MODO.TRABALHANDO || valor === MODO.CONTRATANDO;

function assinar(ouvinte) {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

function notificar() {
  ouvintes.forEach((ouvinte) => ouvinte());
}

function lerGuardado(userId) {
  try {
    const valor = window.localStorage.getItem(chaveDoStorage(userId));
    return ehModo(valor) ? valor : null;
  } catch (erro) {
    return null;
  }
}

/** O modo lembrado para o usuário; "trabalhando" quando nunca escolheu. Síncrono, para snapshot. */
export function lerModo(userId) {
  if (!userId) return MODO.TRABALHANDO;
  const chave = String(userId);
  if (!modos.has(chave)) {
    modos.set(chave, lerGuardado(chave) || MODO.TRABALHANDO);
  }
  return modos.get(chave);
}

export function definirModo(userId, modo) {
  if (!userId || !ehModo(modo) || lerModo(userId) === modo) return;
  const chave = String(userId);
  modos.set(chave, modo);
  try {
    window.localStorage.setItem(chaveDoStorage(chave), modo);
  } catch (erro) {
    /* sem storage o modo vale só nesta sessão */
  }
  notificar();
}

export function lerPendenciasForaDaTela(userId) {
  return userId ? pendenciasForaDaTela.get(String(userId)) || 0 : 0;
}

export function definirPendenciasForaDaTela(userId, quantidade) {
  if (!userId) return;
  const valor = Math.max(0, Number(quantidade) || 0);
  if (lerPendenciasForaDaTela(userId) === valor) return;
  pendenciasForaDaTela.set(String(userId), valor);
  notificar();
}

/** Só para os testes: esquece modos e pendências guardados em memória. */
export function esquecerModos() {
  modos.clear();
  pendenciasForaDaTela.clear();
  notificar();
}

/**
 * Todo login abre no modo trabalhando (decisão do Francisco, 2026-10-15): para quem tem perfil
 * profissional, o trabalho é a razão principal de estar na plataforma, e o pedido de cliente esperando
 * resposta não pode ficar atrás de um modo esquecido de outra sessão. O modo lembrado vale só dentro
 * da sessão. Reabrir o app com a sessão ainda viva não é login e mantém o modo.
 *
 * Apaga o modo de qualquer conta guardada no aparelho, e não só o da que entrou: quem chama ainda não
 * decodificou o token, e a regra é a mesma para todas.
 */
export function voltarAoModoPadrao() {
  modos.clear();
  pendenciasForaDaTela.clear();
  try {
    const chaves = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const chave = window.localStorage.key(i);
      if (chave && chave.startsWith(chaveDoStorage(''))) chaves.push(chave);
    }
    chaves.forEach((chave) => window.localStorage.removeItem(chave));
  } catch (erro) {
    /* sem storage, a memória já foi limpa */
  }
  notificar();
}

/**
 * O modo da conta e o que deriva dele.
 *
 * - `perfilEfetivo`: o que painel e navegação leem no lugar do papel cru. "profissional" só no modo
 *   trabalhando; "usuario" para quem contrata, inclusive o cliente puro. null enquanto o papel não
 *   resolveu, pelo mesmo motivo do usePerfilProfissional: não piscar o rótulo errado.
 * - `podeAlternar`: só quem tem perfil profissional vê o seletor.
 * - `pendenciasOutroModo`: o número do ponto âmbar no seletor, no menu e na aba da conta.
 */
export function useModoConta(userId) {
  const isProfissional = usePerfilProfissional(userId);
  const modoGuardado = useSyncExternalStore(assinar, () => lerModo(userId));
  const pendencias = useSyncExternalStore(assinar, () => lerPendenciasForaDaTela(userId));

  const podeAlternar = isProfissional === true;
  const modo = podeAlternar ? modoGuardado : MODO.CONTRATANDO;

  let perfilEfetivo = null;
  if (isProfissional !== null) {
    perfilEfetivo = podeAlternar && modo === MODO.TRABALHANDO ? 'profissional' : 'usuario';
  }

  const alternar = useCallback((novoModo) => definirModo(userId, novoModo), [userId]);

  return {
    isProfissional,
    modo,
    perfilEfetivo,
    podeAlternar,
    pendenciasOutroModo: podeAlternar ? pendencias : 0,
    alternar,
  };
}

/**
 * Busca e agendar são o lado de quem contrata: a profissional que abre essas telas passa para o
 * modo contratando sem precisar escolher. O caminho contrário não existe; nenhuma tela leva a
 * pessoa de volta para "trabalhando" sem ela pedir.
 */
export function useEntrarNoModoContratando(userId) {
  const { podeAlternar, modo, alternar } = useModoConta(userId);

  useEffect(() => {
    if (podeAlternar && modo !== MODO.CONTRATANDO) {
      alternar(MODO.CONTRATANDO);
    }
  }, [podeAlternar, modo, alternar]);
}
